import { useContext } from "react";
import { AuthContext } from "@hub/auth";

// The shell's identity chrome, read from the AUTHENTICATED principal.
//
// The sidebar used to hardcode `{ initials: 'PB', name: 'Paul Barbagallo',
// role: 'Coder · Cascade pod' }`. That is worse than a placeholder on a screen
// that carries PHI: every deployment showed one real person's name to every
// signed-in user, so a reviewer had no way to notice they were in the wrong
// account, and a screenshot in a ticket attributed someone else's session to
// them. The name must come from the session or not be claimed at all.

/** What the sidebar renders. Shape matches Sidebar's `user` prop. */
export interface SignedInUser {
  initials: string;
  name: string;
  role: string;
}

/** Shown when there is no authenticated principal. Deliberately not a person:
 *  an unauthenticated shell must look unauthenticated. */
export const SIGNED_OUT_USER: SignedInUser = {
  initials: "–",
  name: "Not signed in",
  role: "No active session",
};

/** First letters of the first and last word, uppercased. Falls back to the
 *  leading character so a single-word or email-only identity still renders. */
export function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "–";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** Humanise a role string for display: "eval_manager" → "Eval manager".
 *  A presentation transform of the SERVER's value — never a substitution. */
export function roleLabel(role: string): string {
  const spaced = role.replace(/[_-]+/g, " ").trim();
  if (!spaced) return "";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Map the session's principal onto the sidebar shape. */
export function signedInUserFrom(
  user: { displayName?: string; email?: string; role?: string } | null | undefined,
): SignedInUser {
  if (!user) return SIGNED_OUT_USER;

  // displayName is the server's chosen label; email is the fallback identity
  // it is always able to supply. If neither is present the account is still
  // real, so it renders as an unnamed session rather than as nobody.
  const name = (user.displayName || user.email || "").trim();
  if (!name) {
    return { initials: "–", name: "Signed in", role: roleLabel(user.role ?? "") };
  }

  return {
    initials: initialsFor(name),
    name,
    role: roleLabel(user.role ?? ""),
  };
}

/**
 * The signed-in principal, or the signed-out placeholder.
 *
 * Reads AuthContext DIRECTLY rather than through useAuth(), which throws when
 * no AuthProvider is mounted. The backendless preview route (/shell-preview)
 * is deliberately unauthenticated, so useAuth() there would crash the shell —
 * and the crash would push someone toward re-introducing a hardcoded user to
 * make the preview work again.
 */
export function useSignedInUser(): SignedInUser {
  const auth = useContext(AuthContext);
  return signedInUserFrom(auth?.user);
}

/** The raw role string the SERVER reported for the current principal, or null.
 *  Exposed separately from the display shape because scope decisions must key
 *  off the server's value, never off a humanised label.
 *
 *  LOSSY — it is `user.role`, the single clamped field. Prefer
 *  useSignedInRoles() for anything that describes the caller's access; this
 *  remains for the chrome, which shows one label. */
export function useSignedInRole(): string | null {
  const auth = useContext(AuthContext);
  return auth?.user?.role ?? null;
}

/**
 * EVERY role the server reported for the current principal, or null when the
 * session did not report any.
 *
 * A principal holds several roles routinely — they are IdP group strings, so a
 * team's directory group sits alongside whatever carries a capability. The
 * session used to report only the first, and `user.role` still does, which is
 * why anything answering "what can this caller reach" has to read this instead:
 * ["viewer","auditor"] read as "viewer" and the eval banner told an auditor
 * they were working an assigned queue.
 *
 * null (rather than []) when the field is absent, so a caller can tell "the
 * server reported no roles" from "this session shape predates the field". Both
 * mean claim nothing; neither means deny.
 */
export function useSignedInRoles(): string[] | null {
  const auth = useContext(AuthContext);
  const roles = auth?.user?.roles;
  return Array.isArray(roles) ? roles : null;
}
